import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as geojson from "geojson";
import { api } from "../utils/api";
import { ApiError, ApiResponse } from "../types/api";
import {
  AreaInfo,
  ChangesCollection,
  JobGroups,
  OrderItem,
  DataChangeFeature,
  DataChangeStatus,
  DataReviewJob,
  DataReviewJobOptions,
  DataReviewSummary,
  ImageryOption,
  PaintRequest,
  Remainder,
  TermRequest,
} from "../types/dataReview";
import { invalidateCuring } from "./curing";

const pid = (projectId: string | number | undefined) =>
  projectId !== undefined ? String(projectId) : undefined;

export const dataReviewSummaryKey = (projectId: string | number | undefined) => [
  "project", pid(projectId), "data-review",
];

const jobBusy = (j: DataReviewJob | null | undefined) =>
  !!j && (j.status === "queued" || j.status === "running" || j.status === "applying" || !!j.reverting);

/** The review summary. Polls while a tool runs or changes are being applied or undone. */
export const useDataReviewSummary = (projectId: string | number | undefined) =>
  useQuery({
    queryKey: dataReviewSummaryKey(projectId),
    queryFn: () => api.request<ApiResponse<DataReviewSummary>>(`/api/projects/${projectId}/data-review`),
    enabled: projectId !== undefined && projectId !== "",
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const s = query.state.data?.data;
      if (!s) return false;
      const busy = s.applying || s.reverting || s.jobs.some(jobBusy);
      return busy ? 3000 : false;
    },
  });

/** The photo sources covering a drawn area, to flick through before a run. */
export const useImageryOptions = (
  projectId: string | number | undefined,
  bbox: { west: number; south: number; east: number; north: number } | null,
) =>
  useQuery({
    queryKey: ["project", pid(projectId), "data-review-imagery", bbox ? [bbox.west, bbox.south, bbox.east, bbox.north].map((v) => v.toFixed(5)).join(",") : "none"],
    queryFn: () =>
      api.request<ApiResponse<ImageryOption[]>>(
        `/api/projects/${projectId}/data-review/imagery?west=${bbox!.west}&south=${bbox!.south}&east=${bbox!.east}&north=${bbox!.north}`,
      ),
    enabled: projectId !== undefined && projectId !== "" && !!bbox,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

/** Start the machine that runs the fine-detail model as the editor opens: it
 *  takes a few minutes, so it is usually up by the time a run or a look-for
 *  needs it. Best effort; nothing waits on the answer. */
export const useWakeModelsWorker = (projectId: string | number | undefined) =>
  useMutation<ApiResponse<{ starting: boolean | null }>, ApiError, void>({
    mutationFn: () => api.request(`/api/projects/${projectId}/data-review/worker`, { method: "POST" }),
  });

/** Ask a finished fine-detail run to look for more things in its photo. */
export const useFindMoreTerms = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<{ job: number; terms: string[] }>, ApiError, { jobId: number; terms: TermRequest[] }>({
    mutationFn: ({ jobId, terms }) =>
      api.request(`/api/projects/${projectId}/data-review/jobs/${jobId}/find`, { method: "POST", body: { terms } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: dataReviewSummaryKey(projectId) }),
  });
};

/** How sure a finding from one pass must be to count (0..100). */
export const useSetPassThreshold = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<{ job: number; thresholds: Record<string, number> }>, ApiError, { jobId: number; pass: number; threshold: number }>({
    mutationFn: ({ jobId, ...body }) =>
      api.request(`/api/projects/${projectId}/data-review/jobs/${jobId}/threshold`, { method: "POST", body }),
    onSuccess: () => invalidateReview(qc, projectId),
  });
};

/** Stop a look-for pass; the run keeps its photo and earlier findings. */
export const useStopDataReviewPass = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<{ job: number; stopped: boolean }>, ApiError, number>({
    mutationFn: (jobId) => api.request(`/api/projects/${projectId}/data-review/jobs/${jobId}/stop`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: dataReviewSummaryKey(projectId) }),
  });
};

const invalidateReview = (qc: ReturnType<typeof useQueryClient>, projectId: string | number | undefined) => {
  qc.invalidateQueries({ queryKey: dataReviewSummaryKey(projectId) });
  qc.invalidateQueries({ queryKey: ["project", pid(projectId), "data-review-changes"] });
  qc.invalidateQueries({ queryKey: ["project", pid(projectId), "data-review-groups"] });
  qc.invalidateQueries({ queryKey: ["project", pid(projectId), "data-review-order"] });
};

/** keepPreviousData, but only while the first ``n`` parts of the key stay
 *  the same (the run, the target), so a new cut-off or view keeps the last
 *  answer on screen while another run's or another target's findings are
 *  never shown as this one's (the editor picks a target and a shape from
 *  what it holds). */
const keepWhileSame = (key: readonly unknown[], n: number) =>
  <T,>(prev: T | undefined, prevQuery?: { queryKey: readonly unknown[] }): T | undefined =>
    prevQuery && prevQuery.queryKey.slice(0, n).every((v, i) => v === key[i]) ? prev : undefined;

/** The page's unsaved cut-offs for one run, as the server takes them:
 *  "pass:value,pass:value" from the slider overrides keyed "jobId:pass". */
export const cutoffParam = (jobId: number | null | undefined, overrides: Record<string, number>): string =>
  jobId == null ? "" : Object.entries(overrides)
    .filter(([k]) => k.startsWith(`${jobId}:`))
    .map(([k, v]) => `${k.split(":")[1]}:${v}`)
    .sort()
    .join(",");

/** The same, as a body field: {pass: value}. */
export const cutoffBody = (jobId: number | null | undefined, overrides: Record<string, number>): Record<string, number> =>
  Object.fromEntries(Object.entries(overrides).filter(([k]) => jobId != null && k.startsWith(`${jobId}:`)).map(([k, v]) => [k.split(":")[1], v]));

/** A run's findings counted per target: status, under the cut-off,
 *  histogram, what the accepted ones add up to. No shapes. */
export const useJobGroups = (projectId: string | number | undefined, jobId: number | null | undefined, thr: string) => {
  const queryKey = ["project", pid(projectId), "data-review-groups", jobId ?? 0, thr];
  return useQuery({
    queryKey,
    queryFn: () => api.request<ApiResponse<JobGroups>>(`/api/projects/${projectId}/data-review/jobs/${jobId}/groups${thr ? `?thr=${thr}` : ""}`),
    enabled: projectId !== undefined && projectId !== "" && !!jobId,
    refetchOnWindowFocus: false,
    placeholderData: keepWhileSame(queryKey, 4),
  });
};

/** One target's findings above the cut-off, as ids, in review order. */
export const useReviewOrder = (projectId: string | number | undefined, jobId: number | null | undefined, group: string | null, thr: string) => {
  const queryKey = ["project", pid(projectId), "data-review-order", jobId ?? 0, group ?? "", thr];
  return useQuery({
    queryKey,
    queryFn: () => api.request<ApiResponse<OrderItem[]>>(
      `/api/projects/${projectId}/data-review/jobs/${jobId}/order?group=${encodeURIComponent(group ?? "")}${thr ? `&thr=${thr}` : ""}`,
    ),
    enabled: projectId !== undefined && projectId !== "" && !!jobId && !!group,
    refetchOnWindowFocus: false,
    placeholderData: keepWhileSame(queryKey, 5),
  });
};

export interface MapView { bbox: [number, number, number, number]; zoom: number }

/** The findings in the map's view, above the cut-off, simplified to the
 *  zoom and capped (``truncated`` when some were left out). */
export const useChangesInView = (projectId: string | number | undefined, jobId: number | null | undefined, view: MapView | null, thr: string) => {
  const queryKey = ["project", pid(projectId), "data-review-changes", "view", jobId ?? 0, view ? view.bbox.join(",") : "", view ? Math.round(view.zoom) : 0, thr];
  return useQuery({
    queryKey,
    queryFn: () => api.request<ApiResponse<ChangesCollection>>(
      `/api/projects/${projectId}/data-review/jobs/${jobId}/changes?bbox=${view!.bbox.join(",")}&zoom=${view!.zoom.toFixed(1)}${thr ? `&thr=${thr}` : ""}`,
    ),
    enabled: projectId !== undefined && projectId !== "" && !!jobId && !!view,
    refetchOnWindowFocus: false,
    placeholderData: keepWhileSame(queryKey, 5),
  });
};

const changesByIdsKey = (projectId: string | number | undefined, jobId: number | null | undefined, ids: number[]) =>
  ["project", pid(projectId), "data-review-changes", "ids", jobId ?? 0, ids.join(",")];
const fetchChangesByIds = (projectId: string | number | undefined, jobId: number | null | undefined, ids: number[]) =>
  api.request<ApiResponse<ChangesCollection>>(`/api/projects/${projectId}/data-review/jobs/${jobId}/changes?ids=${ids.join(",")}`);

/** A few findings in full: the shape in hand, the filmstrip. */
export const useChangesByIds = (projectId: string | number | undefined, jobId: number | null | undefined, ids: number[]) => {
  const queryKey = changesByIdsKey(projectId, jobId, ids);
  return useQuery({
    queryKey,
    queryFn: () => fetchChangesByIds(projectId, jobId, ids),
    enabled: projectId !== undefined && projectId !== "" && !!jobId && ids.length > 0,
    refetchOnWindowFocus: false,
    placeholderData: keepWhileSame(queryKey, 5),
  });
};

/** One finding in full, from the cache when it is there. */
export const loadChange = async (
  qc: ReturnType<typeof useQueryClient>, projectId: string | number | undefined, jobId: number, id: number,
): Promise<DataChangeFeature | null> => {
  const res = await qc.fetchQuery({ queryKey: changesByIdsKey(projectId, jobId, [id]), queryFn: () => fetchChangesByIds(projectId, jobId, [id]), staleTime: 10_000 });
  return res.data?.features?.[0] ?? null;
};

/** The findings above the cut-off whose centre a lasso takes in. */
export const selectInPolygon = (
  projectId: string | number | undefined, jobId: number, polygon: geojson.Polygon, group: string | null, thresholds: Record<string, number>,
) => api.request<ApiResponse<{ ids: number[] }>>(
  `/api/projects/${projectId}/data-review/jobs/${jobId}/select`, { method: "POST", body: { polygon, group, thresholds } },
);

/** A candidate area's size and the buildings inside it, before a run exists. */
export const useAreaInfo = (projectId: string | number | undefined) =>
  useMutation<ApiResponse<AreaInfo>, ApiError, geojson.Geometry | geojson.Feature>({
    mutationFn: (area) => api.request(`/api/projects/${projectId}/data-review/area-info`, { method: "POST", body: { area } }),
  });

/** Hand-painted ground on a run: a brush stroke, a lasso fill or a wand
 *  click becomes an accepted change; "erase" takes ground away instead. */
export interface PaintResult {
  change: DataChangeFeature | null;
  touched: number[];
  /** The shapes the op trimmed, as they are now. */
  updated: DataChangeFeature[];
  /** Shapes the op removed: trimmed to nothing, or merged into the new one. */
  removed: number[];
  /** Length of the run's paint journal, for Undo. */
  paint_ops: number;
}

export const usePaintChange = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<PaintResult>, ApiError, { jobId: number } & PaintRequest>({
    mutationFn: ({ jobId, ...body }) =>
      api.request(`/api/projects/${projectId}/data-review/jobs/${jobId}/paint`, { method: "POST", body }),
    // The response says exactly what changed, so the findings already held
    // are updated in place. Downloading them all again after every stroke
    // took seconds on a run with thousands of findings, and each new stroke
    // cancelled the last download, so painted ground showed late or not at
    // all and Undo waited on it.
    onSuccess: (r, v) => {
      const res = r.data;
      if (!res) return;
      const removed = new Set(res.removed ?? []);
      const updated = new Map((res.updated ?? []).map((f) => [f.properties.id, f]));
      for (const [key, old] of qc.getQueriesData<ChangesCache>({ queryKey: ["project", pid(projectId), "data-review-changes"] })) {
        if (!old?.data?.features) continue;
        const features = old.data.features.filter((f) => !removed.has(f.properties.id)).map((f) => updated.get(f.properties.id) ?? f);
        // The new shape belongs on the map; a by-id list asked for others.
        if (res.change && key[3] === "view" && !features.some((f) => f.properties.id === res.change!.properties.id)) features.unshift(res.change);
        qc.setQueryData<ChangesCache>(key, { ...old, data: { ...old.data, features } });
      }
      qc.invalidateQueries({ queryKey: ["project", pid(projectId), "data-review-groups"] });
      qc.invalidateQueries({ queryKey: ["project", pid(projectId), "data-review-order"] });
      qc.setQueryData<ApiResponse<DataReviewSummary>>(dataReviewSummaryKey(projectId), (old) =>
        old?.data
          ? { ...old, data: { ...old.data, jobs: old.data.jobs.map((j) => (j.id === v.jobId ? { ...j, stats: { ...j.stats, paint_ops: res.paint_ops } } : j)) } }
          : old,
      );
    },
  });
};

/** After an apply or revert lands: everything the vegetation and assets feed. */
export const invalidateAfterDataApply = (
  qc: ReturnType<typeof useQueryClient>, projectId: string | number | undefined
) => {
  invalidateReview(qc, projectId);
  qc.invalidateQueries({ queryKey: ["vegetationSources"] });
  invalidateCuring(qc, projectId);
  qc.invalidateQueries({ queryKey: ["strategy"] });
  qc.invalidateQueries({ queryKey: ["strategyJob"] });
  qc.invalidateQueries({ queryKey: ["strategyStatus"] });
  qc.invalidateQueries({ queryKey: ["asset-tiles-url"] });
  qc.invalidateQueries({ queryKey: ["assets"] });
  qc.setQueryData<number>(["vegetation-tile-version", pid(projectId)], () => Date.now());
};

export const useStartDataReviewJob = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<DataReviewJob>, ApiError, { area?: geojson.Geometry | geojson.Feature | null; options?: DataReviewJobOptions }>({
    mutationFn: (body) =>
      api.request<ApiResponse<DataReviewJob>>(`/api/projects/${projectId}/data-review/jobs`, { method: "POST", body }),
    onSuccess: (r) => {
      // The new run is in the summary at once, so a screen that opens on
      // it does not find it missing while the refetch is on its way.
      const job = r.data;
      if (job) {
        qc.setQueryData<ApiResponse<DataReviewSummary>>(dataReviewSummaryKey(projectId), (old) =>
          old?.data ? { ...old, data: { ...old.data, jobs: [job, ...old.data.jobs.filter((j) => j.id !== job.id)] } } : old,
        );
      }
      invalidateReview(qc, projectId);
    },
  });
};

export interface OutlineEdit { op: "add" | "subtract"; geometry: geojson.Geometry | geojson.Feature }

type ChangesCache = ApiResponse<ChangesCollection>;

/** The editor's grouping of a finding (the server groups the same way). */
export const groupOfProps = (p: DataChangeFeature["properties"]): string => {
  const a = p.attrs ?? {};
  if (a.painted) return "painted";
  if (a.term) return String(a.term);
  if (a.pass !== undefined && a.pass !== null) return `pass:${a.pass}`;
  return "other";
};
type Rollback = { rollback?: () => void };

/** Change the cached findings at once, so a decision shows the moment it is
 *  made; the server catches up and the refetch afterwards confirms it.
 *  ``patch`` returns the new feature, or null to drop it. Returns a rollback
 *  for when the server refuses. */
const patchCachedChanges = async (
  qc: ReturnType<typeof useQueryClient>, projectId: string | number | undefined,
  match: (p: DataChangeFeature["properties"]) => boolean, patch: (f: DataChangeFeature) => DataChangeFeature | null,
): Promise<() => void> => {
  const key = ["project", pid(projectId), "data-review-changes"];
  await qc.cancelQueries({ queryKey: key });
  const before = qc.getQueriesData<ChangesCache>({ queryKey: key });
  qc.setQueriesData<ChangesCache>({ queryKey: key }, (old) => {
    if (!old?.data?.features) return old;
    const features = old.data.features.flatMap((f) => (match(f.properties) ? (patch(f) ?? []) : [f]));
    return { ...old, data: { ...old.data, features } };
  });
  return () => before.forEach(([k, data]) => qc.setQueryData<ChangesCache>(k, data));
};

const decided = (f: DataChangeFeature, v: { status?: DataChangeStatus; target?: string; asset_type?: string }): DataChangeFeature => ({
  ...f,
  properties: {
    ...f.properties,
    ...(v.status ? { status: v.status } : {}),
    ...(v.target ? { target: v.target } : {}),
    ...(v.asset_type !== undefined ? { attrs: { ...f.properties.attrs, asset_type: v.asset_type } } : {}),
  },
});

export const useUpdateDataChange = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<DataChangeFeature>, ApiError, { id: number; status?: DataChangeStatus; edit?: OutlineEdit }, Rollback>({
    mutationFn: ({ id, ...body }) =>
      api.request<ApiResponse<DataChangeFeature>>(`/api/projects/${projectId}/data-review/changes/${id}`, { method: "PATCH", body }),
    // A decision shows at once; an outline edit waits for the server, which
    // works out the new shape.
    onMutate: async (v) => {
      if (v.edit || !v.status) return {};
      return { rollback: await patchCachedChanges(qc, projectId, (p) => p.id === v.id, (f) => decided(f, v)) };
    },
    onError: (_e, _v, ctx) => ctx?.rollback?.(),
    onSettled: () => invalidateReview(qc, projectId),
  });
};

export const useBulkDataChanges = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<{ updated: number }>, ApiError, {
    status?: DataChangeStatus; job_id?: number; ids?: number[];
    /** Re-target instead of (or as well as) deciding. */
    target?: string; asset_type?: string; only_pending?: boolean;
    /** Remove the matched changes instead (a look-for target taken back). */
    delete?: boolean;
    /** One target of a run, as the editor groups them. */
    group?: string;
    /** Unsaved cut-offs for the run, {pass: value}. */
    thresholds?: Record<string, number>;
    /** Re-target or remove findings under the cut-off too. */
    include_hidden?: boolean;
  }, Rollback>({
    mutationFn: (body) =>
      api.request<ApiResponse<{ updated: number }>>(`/api/projects/${projectId}/data-review/changes/bulk`, { method: "POST", body }),
    // The same filters the server applies, on the cached findings, at once.
    onMutate: async (v) => {
      if (!v.delete && !v.status && v.target === undefined && v.asset_type === undefined) return {};
      const onlyPending = v.only_pending ?? (!!v.status && !v.delete);
      const ids = v.ids ? new Set(v.ids) : null;
      const match = (p: DataChangeFeature["properties"]) =>
        p.status !== "applied" && (!ids || ids.has(p.id)) && (v.job_id === undefined || p.job_id === v.job_id)
        && (v.group === undefined || groupOfProps(p) === v.group)
        && (!onlyPending || p.status === "pending");
      return { rollback: await patchCachedChanges(qc, projectId, match, (f) => (v.delete ? null : decided(f, v))) };
    },
    onError: (_e, _v, ctx) => ctx?.rollback?.(),
    onSettled: () => invalidateReview(qc, projectId),
  });
};

export const useApplyDataReview = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<{ jobs: number[] }>, ApiError, { job_id: number; remainder?: Remainder | null }>({
    mutationFn: (body) =>
      api.request<ApiResponse<{ jobs: number[] }>>(`/api/projects/${projectId}/data-review/apply`, { method: "POST", body }),
    onSuccess: () => invalidateReview(qc, projectId),
  });
};

export const useRevertDataReviewJob = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<{ job: number }>, ApiError, number>({
    mutationFn: (jobId) =>
      api.request<ApiResponse<{ job: number }>>(`/api/projects/${projectId}/data-review/jobs/${jobId}/revert`, { method: "POST", body: {} }),
    // The undo runs as a task and the run reads "applied" until it lands;
    // flagged at once, the summary polls and the Undo button goes.
    onSuccess: (_r, jobId) => {
      qc.setQueryData<ApiResponse<DataReviewSummary>>(dataReviewSummaryKey(projectId), (old) =>
        old?.data
          ? { ...old, data: { ...old.data, reverting: true, jobs: old.data.jobs.map((j) => (j.id === jobId ? { ...j, reverting: true } : j)) } }
          : old,
      );
      invalidateReview(qc, projectId);
    },
  });
};

/** An apply or an undo that finished between two looks at the runs. */
export interface DataReviewLanding { job: DataReviewJob; kind: "applied" | "apply-failed" | "reverted" | "revert-failed" }

/** What landed between ``before`` and ``after``. An undo never passes
 *  through "applying" (the run reads "applied" with ``reverting`` set until
 *  it lands), so each run's own status and flag are compared. */
export const landedJobs = (before: DataReviewJob[], after: DataReviewJob[]): DataReviewLanding[] => {
  const prev = new Map(before.map((j) => [j.id, j]));
  const out: DataReviewLanding[] = [];
  for (const j of after) {
    const p = prev.get(j.id);
    if (!p) continue;
    if (p.reverting && !j.reverting) out.push({ job: j, kind: j.status === "reverted" ? "reverted" : "revert-failed" });
    else if (j.status === "reverted" && p.status !== "reverted") out.push({ job: j, kind: "reverted" });
    else if (j.status === "applied" && p.status !== "applied") out.push({ job: j, kind: "applied" });
    else if (p.status === "applying" && j.status !== "applying") out.push({ job: j, kind: "apply-failed" });
  }
  return out;
};

/** Calls ``onLanded`` with each apply or undo that lands while mounted. */
export const useDataReviewLandings = (projectId: string | number | undefined, onLanded: (landed: DataReviewLanding[]) => void) => {
  const { data } = useDataReviewSummary(projectId);
  const jobs = data?.data?.jobs;
  const seen = useRef<{ project: string | undefined; jobs: DataReviewJob[] } | null>(null);
  const callback = useRef(onLanded);
  callback.current = onLanded;
  useEffect(() => {
    if (!jobs) return;
    const last = seen.current;
    const before = last && last.project === pid(projectId) ? last.jobs : null;
    seen.current = { project: pid(projectId), jobs };
    if (!before) return;
    const landed = landedJobs(before, jobs);
    if (landed.length) callback.current(landed);
  }, [jobs, projectId]);
};

/** Throw away a run that has not been applied, proposals and all. */
export const useDiscardDataReviewJob = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<{ deleted: number }>, ApiError, number>({
    mutationFn: (jobId) =>
      api.request<ApiResponse<{ deleted: number }>>(`/api/projects/${projectId}/data-review/jobs/${jobId}`, { method: "DELETE" }),
    onSuccess: () => invalidateReview(qc, projectId),
  });
};

/** Take back the last paint op, or every op after the ``to``-th (Cancel in
 *  paint mode reverts to where it started). */
export const useUndoPaint = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<{ undone: number; paint_ops: number }>, ApiError, { jobId: number; to?: number }>({
    mutationFn: ({ jobId, to }) =>
      api.request(`/api/projects/${projectId}/data-review/jobs/${jobId}/paint/undo`, { method: "POST", body: to === undefined ? {} : { to } }),
    onSuccess: () => invalidateReview(qc, projectId),
  });
};

/** Grow a region like the clicked pixel (from the run's own raster) into the change. */
export const useWandDataChange = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<DataChangeFeature>, ApiError, { id: number; lng: number; lat: number; tolerance: number; op: "add" | "subtract" }>({
    mutationFn: ({ id, ...body }) =>
      api.request<ApiResponse<DataChangeFeature>>(`/api/projects/${projectId}/data-review/changes/${id}/wand`, { method: "POST", body }),
    onSuccess: () => invalidateReview(qc, projectId),
  });
};

/** Put the outline back the way it was before the last edit. */
export const useUndoDataChange = (projectId: string | number | undefined) => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<DataChangeFeature>, ApiError, number>({
    mutationFn: (id) =>
      api.request<ApiResponse<DataChangeFeature>>(`/api/projects/${projectId}/data-review/changes/${id}/undo`, { method: "POST", body: {} }),
    onSuccess: () => invalidateReview(qc, projectId),
  });
};
